import { useApp } from '../store/app';
import { useDrive } from '../store/drive';
import { activateDrive } from '../lib/sources';
import { openBook } from '../player/engine';
import { bookRatio, knownTotalDuration, remainingSeconds } from '../lib/bookMath';
import { formatRemaining, formatTime } from '../lib/format';
import { Cover, Equalizer, NmButton, NmCard, ProgressLine } from './nm';
import { AlertIcon, BookIcon, CheckIcon } from './icons';
import type { Book } from '../types';

export function Library() {
   const books = useApp((s) => s.books);
   const status = useApp((s) => s.libraryStatus);
   const error = useApp((s) => s.libraryError);

   if (status === 'loading' && books.length === 0) {
      return (
         <>
            <ReconnectNotice />
            <LibrarySkeleton />
         </>
      );
   }

   if (status === 'error' && books.length === 0) {
      return (
         <NmCard className="notice notice--error">
            <AlertIcon size={22} />
            <div>
              <h2 className="notice__title">Could not load your library</h2>
              <p className="notice__body">{error}</p>
            </div>
         </NmCard>
      );
   }

   if (books.length === 0) {
      return (
         <NmCard className="notice">
            <BookIcon size={22} />
            <div>
              <h2 className="notice__title">No audiobooks found</h2>
              <p className="notice__body">
                 Nothing in this folder looked like audio. Pick a different folder in Settings, or
                 check that the files are one of mp3, m4a, m4b, aac, opus, ogg, flac or wav.
              </p>
            </div>
         </NmCard>
      );
   }

   return (
      <>
         <ReconnectNotice />
         {error && (
            <NmCard className="notice notice--warn">
              <AlertIcon size={20} />
              <p className="notice__body">
                 Showing your last saved library — refreshing failed: {error}
              </p>
            </NmCard>
         )}
         <ul className="grid">
            {books.map((book, i) => (
              // --i drives the entrance stagger; capped so a large library does not
              // end with cards appearing seconds late.
              <li key={book.id} style={{ '--i': Math.min(i, 14) } as React.CSSProperties}>
                 <BookCard book={book} />
              </li>
            ))}
         </ul>
      </>
   );
}

/** Shown when a Drive folder is configured but the Google session has lapsed.
  * Rather than dropping you into the demo library with no explanation, this
  * says what happened and reconnects in one click. */
function ReconnectNotice() {
   const needsSignIn = useDrive((s) => s.needsSignIn);
   const root = useDrive((s) => s.root);
   const status = useDrive((s) => s.status);

   if (!needsSignIn || !root) return null;

   return (
      <NmCard className="notice notice--warn">
         <AlertIcon size={20} />
         <div>
            <h2 className="notice__title">Signed out of Google</h2>
            <p className="notice__body">
              Showing the demo library. Reconnect to load “{root.name}” from Drive again.
            </p>
         </div>
         <NmButton
          className="nmbtn--primary notice__action"
          onClick={() => void activateDrive(root)}
          disabled={status === 'connecting'}
      >
        {status === 'connecting' ? 'Connecting…' : 'Reconnect'}
      </NmButton>
    </NmCard>
  );
}

function BookCard({ book }: { book: Book }) {
  const entry = useApp((s) => s.progress.books[book.id]);
  const navigate = useApp((s) => s.navigate);
  const isCurrent = useApp((s) => s.currentBookId === book.id);
  const playing = useApp((s) => s.playing);

  const ratio = bookRatio(book, entry);
  const total = knownTotalDuration(book);
  const remaining = remainingSeconds(book, entry);
  const started = Boolean(entry) && ratio > 0.001;

  const open = () => {
     navigate({ name: 'player', bookId: book.id });
     void openBook(book, { autoplay: false });
  };

  const status =
     entry?.finished ? 'Finished'
     : started ? formatRemaining(remaining)
     : total > 0 ? formatTime(total)
     : `${book.tracks.length} ${book.tracks.length === 1 ? 'file' : 'files'}`;

  return (
     <button
       type="button"
       onClick={open}
       className="nm-raised nm-press bookcard"
       data-current={isCurrent || undefined}
     >
       <span className="nm-inset nm-sm bookcard__frame">
         <Cover title={book.title} url={book.coverUrl} />
         {isCurrent && <Equalizer playing={playing} />}
         {entry?.finished && !isCurrent && (
            <span className="bookcard__done" aria-hidden="true">
              <CheckIcon size={16} />
            </span>
         )}
       </span>

       <span className="bookcard__title">{book.title}</span>
       {book.author && <span className="bookcard__author">{book.author}</span>}

       <span className="bookcard__foot">
         <ProgressLine ratio={ratio} />
         <span className="bookcard__status">{status}</span>
       </span>
     </button>
  );
}

function LibrarySkeleton() {
  return (
     <ul className="grid" aria-hidden="true">
       {Array.from({ length: 6 }, (_, i) => (
         <li key={i}>
            <div className="nm-raised bookcard bookcard--skeleton">
              <span className="nm-inset nm-sm bookcard__frame" />
              <span className="skeleton-line" style={{ width: '76%' }} />
              <span className="skeleton-line" style={{ width: '48%' }} />
            </div>
         </li>
       ))}
     </ul>
  );
}
